import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import { StubAssetsAudit } from "../../../utils/@types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "../../YouTubeScorecard/Components/Tables/tables.css";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";
import {
  MusicAuditColumnVisibilityDialog,
  type MusicAuditColumnConfig,
} from "./MusicAuditColumnVisibilityDialog";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";
import { withExportDate } from "../../../utils/exportFileName";

interface StubAssetsAuditTableProps {}

type ColumnConfig = MusicAuditColumnConfig;

// --- New FilterBar Component ---
const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
  onAggregate,
  onColumnVisibility,
}: any) => {
  // Generate options from unfiltered data
  const legalEntityOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntity))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntityType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem" }}>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.asset_id ? [filters.asset_id] : []}
            value={(e) => {
              if (typeof e === "function") {
                const newArray = e([]);
                setFilters((prev: any) => ({
                  ...prev,
                  asset_id: newArray[0] || "",
                }));
              } else {
                setFilters((prev: any) => ({ ...prev, asset_id: e[0] || "" }));
              }
            }}
            label={"Asset ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.custom_id}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, custom_id: items }))
            }
            label={"Custom ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.embedded_asset_id}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, embedded_asset_id: items }))
            }
            label={"Embedded Asset ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.SoundRecordingId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, SoundRecordingId: items }))
            }
            label={"Sound Recording ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity
          </text>
          <MultiSelect
            value={filters.legalEntity}
            options={legalEntityOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, legalEntity: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity Type
          </text>
          <MultiSelect
            value={filters.legalEntityType}
            options={legalEntityTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntityType: e.value,
              }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <div
            className="flex align-items-center gap-2"
            style={{ marginTop: "1.5rem" }}
          >
            <Checkbox
              checked={filters.highPriority || false}
              onChange={(e) =>
                setFilters((prev: any) => ({
                  ...prev,
                  highPriority: e.checked || false,
                }))
              }
            />
            <label className="text-sm font-medium" style={{ color: "grey" }}>
              High Priority Only
            </label>
          </div>
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Aggregate"
              icon="pi pi-chart-bar"
              onClick={onAggregate}
              severity="secondary"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Columns"
              icon="pi pi-columns"
              onClick={onColumnVisibility}
              severity="secondary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const StubAssetsAuditTable: React.FC<StubAssetsAuditTableProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      asset_id: parseStringParam(params, "asset_id"),
      custom_id: parseStringArrayParam(params, "custom_id"),
      embedded_asset_id: parseStringArrayParam(params, "embedded_asset_id"),
      SoundRecordingId: parseStringArrayParam(params, "SoundRecordingId"),
      legalEntity: parseStringArrayParam(params, "legalEntity"),
      legalEntityType: parseStringArrayParam(params, "legalEntityType"),
      highPriority: parseBooleanParam(params, "highPriority") || false,
    };
  }, [location.search]);

  const dt = useRef<DataTable<StubAssetsAudit[]>>(null);
  const toast = useRef<Toast>(null);
  const initialFiltersApplied = useRef(false);
  const [rows, setRows] = useState<StubAssetsAudit[]>([]);
  const [filters, setFilters] = useState<any>(initialFilters);
  const [unfilteredData, setUnfilteredData] = useState<StubAssetsAudit[]>([]);
  const [filteredRows, setFilteredRows] = useState<StubAssetsAudit[]>([]);
  const [aggregation, setAggregation] = useState<AggregationItem[] | null>(
    null
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [columnVisibilityDialog, setColumnVisibilityDialog] = useState(false);

  // Define the aggregation item type
  type AggregationItem = {
    custom_id: string;
    totalDailyViews: number;
    count: number;
    avgDailyViews: number;
  };

  // Column configuration with visibility controls
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { field: "isHighPriority", header: "Priority", visible: true },
    { field: "asset_id", header: "Asset ID", sortable: true, visible: true },
    { field: "custom_id", header: "Custom ID", sortable: true, visible: true },
    {
      field: "embedded_asset_id",
      header: "Embedded Asset ID",
      sortable: true,
      visible: true,
    },
    {
      field: "SoundRecordingId",
      header: "Sound Recording ID",
      sortable: true,
      visible: true,
    },
    {
      field: "approx_daily_views",
      header: "Approx Daily Views",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntity",
      header: "Legal Entity",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntityType",
      header: "Legal Entity Type",
      sortable: true,
      visible: true,
    },
    { field: "checked", header: "Checked", visible: true },
    { field: "notes", header: "Notes", visible: true },
    {
      field: "createdAt",
      header: "Created At",
      sortable: true,
      visible: true,
    },
    {
      field: "lastUpdatedAt",
      header: "Last Updated",
      sortable: true,
      visible: true,
    },
  ]);

  useEffect(() => {
    apiService.stubAssetsAudit.getStubAssetsWithSRLink({}).then((data: any) => {
      setUnfilteredData(data);
      setRows(data);
      setFilteredRows(data);
    });
  }, []);

  // Simplified priority detection using server-calculated fields
  const isHighPriorityRow = (row: StubAssetsAudit): boolean => {
    return row.isHighPriority || false;
  };

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    let filtered = rows;
    if (filters.asset_id)
      filtered = filtered.filter((r) =>
        r.asset_id?.toLowerCase().includes(filters.asset_id.toLowerCase())
      );
    if (filters.custom_id?.length)
      filtered = filtered.filter((r) =>
        filters.custom_id.some((v: string) =>
          r.custom_id?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.embedded_asset_id?.length)
      filtered = filtered.filter((r) =>
        filters.embedded_asset_id.some((v: string) =>
          r.embedded_asset_id?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.SoundRecordingId?.length)
      filtered = filtered.filter((r) =>
        filters.SoundRecordingId.some((v: string) =>
          r.SoundRecordingId?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.highPriority)
      filtered = filtered.filter((r) => r.isHighPriority === true);
    if (filters.legalEntity?.length)
      filtered = filtered.filter((r) =>
        filters.legalEntity.includes(r.LegalEntity)
      );
    if (filters.legalEntityType?.length)
      filtered = filtered.filter((r) =>
        filters.legalEntityType.includes(r.LegalEntityType)
      );
    setFilteredRows(filtered);
    const query = buildQueryString({
      asset_id: filters.asset_id,
      custom_id: filters.custom_id,
      embedded_asset_id: filters.embedded_asset_id,
      SoundRecordingId: filters.SoundRecordingId,
      legalEntity: filters.legalEntity,
      legalEntityType: filters.legalEntityType,
      highPriority: filters.highPriority,
    });
    navigate({ search: query }, { replace: true });
  }, [filters, navigate, rows]);

  const handleReset = () => {
    setFilters({
      asset_id: "",
      custom_id: [],
      embedded_asset_id: [],
      SoundRecordingId: [],
      highPriority: false,
      legalEntity: [],
      legalEntityType: [],
    });
    setFilteredRows(rows);
    navigate({ search: "" }, { replace: true });
  };

  useEffect(() => {
    const hasInitialFilters = Object.values(initialFilters).some((value) => {
      if (typeof value === "boolean") return value;
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    if (!hasInitialFilters || rows.length === 0 || initialFiltersApplied.current)
      return;
    handleSearch();
    initialFiltersApplied.current = true;
  }, [initialFilters, rows, handleSearch]);

  const onRowEditComplete = (e: any) => {
    let _rows = [...rows];
    let { newData, index } = e;
    _rows[index] = newData;
    setRows(_rows);
    apiService.stubAssetsAudit.updateStubAssetsWithSRLink(newData);
  };

  const checkedBodyTemplate = (rowData: StubAssetsAudit) => {
    return (
      <Checkbox
        onChange={(e) => {
          let _rows = [...rows];
          const index = _rows.findIndex((r) => r.asset_id === rowData.asset_id);
          _rows[index].checked = e.checked;
          setRows(_rows);
          apiService.stubAssetsAudit.updateStubAssetsWithSRLink({
            asset_id: rowData.asset_id,
            checked: e.checked,
          });
        }}
        checked={rowData.checked ?? false}
      ></Checkbox>
    );
  };

  const notesEditor = (options: any) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback && options.editorCallback(e.target.value)
        }
      />
    );
  };

  const priorityBodyTemplate = (rowData: StubAssetsAudit) => {
    const isHighPriority = isHighPriorityRow(rowData);

    const getPriorityText = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Views",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    const getPriorityTooltip = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Daily Views (>25,000)",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    return (
      <div className="flex align-items-center gap-2">
        {rowData.isPriority && (
          <span
            className="text-yellow-600 font-semibold text-xs"
            title="Manual Priority"
          >
            Manual
          </span>
        )}
        {isHighPriority && (
          <span
            className="text-red-600 font-semibold text-xs px-2 py-1 bg-red-100 rounded"
            title={getPriorityTooltip()}
          >
            {getPriorityText()}
          </span>
        )}
      </div>
    );
  };

  const aggregateData = () => {
    // Group by custom_id and sum daily views
    const customIdAggregation = filteredRows.reduce((acc, row) => {
      const customId = row.custom_id || "Unknown";
      if (!acc[customId]) {
        acc[customId] = {
          custom_id: customId,
          totalDailyViews: 0,
          count: 0,
          avgDailyViews: 0,
        };
      }
      acc[customId].totalDailyViews += row.approx_daily_views || 0;
      acc[customId].count += 1;
      return acc;
    }, {} as Record<string, AggregationItem>);

    // Calculate average daily views for each custom_id
    Object.values(customIdAggregation).forEach((item) => {
      item.avgDailyViews =
        item.count > 0 ? Math.round(item.totalDailyViews / item.count) : 0;
    });

    // Sort by total daily views (descending)
    const sortedAggregation = Object.values(customIdAggregation).sort(
      (a, b) => b.totalDailyViews - a.totalDailyViews
    );

    setAggregation(sortedAggregation);
    setDialogVisible(true);
  };

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible } : col))
    );
  };

  const resetColumnVisibility = () => {
    setColumnConfig((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const isColumnVisible = (field: string): boolean => {
    const column = columnConfig.find((col) => col.field === field);
    return column?.visible === true || false;
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = () => {
    const totalIssues = filteredRows.length;
    const uniqueCustomIds = new Set(filteredRows.map((row) => row.custom_id))
      .size;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const newIssues = filteredRows.filter((row) => {
      if (!row.createdAt) return false;
      const createDate = new Date(row.createdAt);
      return createDate >= sevenDaysAgo;
    }).length;

    const totalApproxDailyViews = filteredRows.reduce((sum, row) => {
      return sum + (row.approx_daily_views || 0);
    }, 0);

    const fixableStubs = filteredRows.filter((row) => {
      const sr = row.SoundRecordingId;
      return sr != null && String(sr).trim() !== "";
    }).length;

    return {
      totalIssues,
      uniqueCustomIds,
      newIssues,
      totalApproxDailyViews,
      fixableStubs,
    };
  };

  const summaryMetrics = calculateSummaryMetrics();

  const handleCellClick = (event: any) => {
    // PrimeReact DataTable onCellClick provides the cell data in event.value
    const cellValue = event.value;

    if (cellValue && String(cellValue).trim()) {
      const valueToCopy = String(cellValue).trim();
      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => {
          toast.current?.show({
            severity: "success",
            summary: "Copied!",
            detail: `"${valueToCopy}" copied to clipboard`,
            life: 2000,
          });
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 2000,
          });
        });
    }
  };

  return (
    <div style={{ width: "100%", marginTop: "1.25rem", marginBottom: "-30px" }}>
      <Toast ref={toast} />
      <Dialog
        header="Custom ID Aggregation"
        visible={dialogVisible}
        style={{ width: "70vw" }}
        onHide={() => setDialogVisible(false)}
      >
        <div className="mb-3">
          <p className="text-sm text-gray-600">
            Summary of daily views grouped by Custom ID (showing{" "}
            {aggregation?.length || 0} custom IDs)
          </p>
        </div>

        {aggregation && aggregation.length > 0 ? (
          <DataTable
            value={aggregation}
            size="small"
            showGridlines
            stripedRows
            tableStyle={{ fontSize: "0.9rem" }}
          >
            <Column
              field="custom_id"
              header="Custom ID"
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="count"
              header="Issue Count"
              sortable
              style={{ minWidth: "120px" }}
              body={(rowData) => rowData.count.toLocaleString()}
            />
            <Column
              field="totalDailyViews"
              header="Total Daily Views"
              sortable
              style={{ minWidth: "150px" }}
              body={(rowData) => rowData.totalDailyViews.toLocaleString()}
            />
            <Column
              field="avgDailyViews"
              header="Average Daily Views"
              sortable
              style={{ minWidth: "150px" }}
              body={(rowData) => rowData.avgDailyViews.toLocaleString()}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4">
            <p className="text-gray-500">No data available for aggregation</p>
          </div>
        )}

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Export CSV"
            icon="pi pi-download"
            onClick={() => {
              if (aggregation && aggregation.length > 0) {
                const csvContent = [
                  [
                    "Custom ID",
                    "Issue Count",
                    "Total Daily Views",
                    "Average Daily Views",
                  ],
                  ...aggregation.map((item) => [
                    item.custom_id,
                    item.count.toString(),
                    item.totalDailyViews.toString(),
                    item.avgDailyViews.toString(),
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");

                const blob = new Blob([csvContent], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${withExportDate("Stub Asset Audit - Custom ID Aggregation")}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              }
            }}
            severity="success"
            outlined
          />
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setDialogVisible(false)}
          />
        </div>
      </Dialog>
      <MusicAuditColumnVisibilityDialog
        visible={columnVisibilityDialog}
        columns={columnConfig}
        onHide={() => setColumnVisibilityDialog(false)}
        onVisibilityChange={handleColumnVisibilityChange}
        onReset={resetColumnVisibility}
      />

      {/* Summary Metrics Section */}
      <Card
        className="mb-3"
        style={{
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div
          className="flex flex-column md:flex-row flex-wrap md:flex-nowrap w-full align-items-stretch justify-content-between gap-3 md:gap-2"
          style={{ rowGap: "0.75rem" }}
        >
          <div className="flex-1 text-center p-3" style={{ minWidth: "min(100%, 8rem)" }}>
            <div className="text-2xl font-bold text-blue-600">
              {summaryMetrics.totalIssues.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Issues</div>
          </div>
          <div className="flex-1 text-center p-3" style={{ minWidth: "min(100%, 8rem)" }}>
            <div className="text-2xl font-bold text-green-600">
              {summaryMetrics.uniqueCustomIds.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Unique Custom IDs</div>
          </div>
          <div className="flex-1 text-center p-3" style={{ minWidth: "min(100%, 8rem)" }}>
            <div className="text-2xl font-bold text-teal-600">
              {summaryMetrics.fixableStubs.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Fixable Stubs</div>
          </div>
          <div className="flex-1 text-center p-3" style={{ minWidth: "min(100%, 8rem)" }}>
            <div className="text-2xl font-bold text-orange-600">
              {summaryMetrics.newIssues.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">New Issues (7 days)</div>
          </div>
          <div className="flex-1 text-center p-3" style={{ minWidth: "min(100%, 8rem)" }}>
            <div className="text-2xl font-bold text-purple-600">
              {summaryMetrics.totalApproxDailyViews.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Daily Views</div>
          </div>
        </div>
      </Card>

      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
          onAggregate={aggregateData}
          onColumnVisibility={() => setColumnVisibilityDialog(true)}
        />
        <DataTable
          ref={dt}
          exportFilename={withExportDate("Stub Asset Audit")}
          value={filteredRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          onCellClick={handleCellClick}
        >
          {isColumnVisible("asset_id") && (
            <Column field="asset_id" header="Asset ID" sortable />
          )}
          {isColumnVisible("custom_id") && (
            <Column field="custom_id" header="Custom ID" sortable />
          )}
          {isColumnVisible("embedded_asset_id") && (
            <Column
              field="embedded_asset_id"
              header="Embedded Asset ID"
              sortable
            />
          )}
          {isColumnVisible("SoundRecordingId") && (
            <Column
              field="SoundRecordingId"
              header="Sound Recording ID"
              sortable
            />
          )}
          {isColumnVisible("approx_daily_views") && (
            <Column
              field="approx_daily_views"
              header="Approx Daily Views"
              body={(rowData) =>
                rowData.approx_daily_views?.toLocaleString() || "0"
              }
              sortable
            />
          )}
          {isColumnVisible("isHighPriority") && (
            <Column
              body={priorityBodyTemplate}
              header="Priority"
              sortable
              field="isHighPriority"
            />
          )}
          {isColumnVisible("LegalEntity") && (
            <Column
              field="LegalEntity"
              header="Legal Entity"
              sortable
            />
          )}
          {isColumnVisible("LegalEntityType") && (
            <Column
              field="LegalEntityType"
              header="Legal Entity Type"
              sortable
            />
          )}
          <Column
            rowEditor
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          {isColumnVisible("checked") && (
            <Column body={checkedBodyTemplate} header="Checked" />
          )}
          {isColumnVisible("notes") && (
            <Column
              field="notes"
              header="Notes"
              editor={notesEditor}
              sortable
            />
          )}
          {isColumnVisible("createdAt") && (
            <Column
              field="createdAt"
              header="Created At"
              sortable
              body={(row: StubAssetsAudit) =>
                row.createdAt
                  ? new Date(row.createdAt).toLocaleString()
                  : "—"
              }
            />
          )}
          {isColumnVisible("lastUpdatedAt") && (
            <Column
              field="lastUpdatedAt"
              header="Last Updated"
              sortable
              body={(row: StubAssetsAudit) =>
                row.lastUpdatedAt
                  ? new Date(row.lastUpdatedAt).toLocaleString()
                  : "—"
              }
            />
          )}
        </DataTable>
      </Card>
    </div>
  );
};

export default StubAssetsAuditTable;
